import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserType } from "../../hooks/useUserType";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";
import styles from "./BottomNav.module.css";

const HOME_ROUTES = ["/", "/files", "/get-ready", "/track", "/share"];

const NAV_ITEMS: {
	key: string;
	icon: string;
	path: string;
}[] = [
	{
		key: "home",
		icon: "/images/bottom-black-nav-home.svg",
		path: "/",
	},
	{
		key: "journey",
		icon: "/images/bottom-black-nav-journey.svg",
		path: "/journey",
	},
	{
		key: "chat",
		icon: "/images/bottom-black-nav-chat.svg",
		path: "/chat",
	},
	{
		key: "communication",
		icon: "/images/bottom-black-nav-communication.svg",
		path: "/communication",
	},
	{
		key: "education",
		icon: "/images/bottom-black-nav-education.svg",
		path: "/education",
	},
];

const HIDDEN_ROUTES = [
	"/login",
	"/callback",
	"/2fa",
	"/onboarding",
	"/onboarding/thank-you",
	"/invitation",
];

export function BottomNav() {
	const { t } = useClientTranslations("common");
	const { isD2C } = useUserType();
	const location = useLocation();
	const navigate = useNavigate();
	const analytics = useAnalytics();

	const isHidden = HIDDEN_ROUTES.some((route) =>
		location.pathname.startsWith(route),
	);

	useEffect(() => {
		if (!isHidden) {
			document.documentElement.classList.add("has-bottom-nav");
		} else {
			document.documentElement.classList.remove("has-bottom-nav");
		}
		return () => {
			document.documentElement.classList.remove("has-bottom-nav");
		};
	}, [isHidden]);

	if (isHidden) {
		return null;
	}

	const handleNavClick = (key: string, path: string) => {
		const navigated = location.pathname !== path;
		analytics.logNav("item_clicked", {
			bar: "bottom",
			item: key,
			destination: path,
			from: location.pathname,
			// false = tapped the already-active item (no navigation occurred).
			navigated,
		});
		if (navigated) {
			navigate(path);
		}
	};

	return (
		<nav className={styles.nav}>
			{NAV_ITEMS.filter((item) => !(isD2C && item.key === "communication")).map(
				(item) => {
					const isActive =
						item.path === "/"
							? HOME_ROUTES.includes(location.pathname)
							: location.pathname === item.path ||
								location.pathname.startsWith(`${item.path}/`);
					return (
						<button
							key={item.key}
							type="button"
							className={`${styles.navItem} ${isActive ? styles.active : ""}`}
							onClick={() => handleNavClick(item.key, item.path)}
							aria-label={t(`navigation.${item.key}`)}
						>
							<img
								src={item.icon}
								alt={t(`navigation.${item.key}`)}
								className={styles.navIcon}
							/>
						</button>
					);
				},
			)}
		</nav>
	);
}
